import { Box, Typography } from "@mui/material";
import React from "react";

import { CARD_TYPE_CONFIG } from "@/cards/common/constant/app-deck";
import { AllTalentFilterOptionProps } from "../../types";

/** Circular "All" bubble shown at the start of the talent-filter strip. */
const AllTalentFilterOption: React.FC<AllTalentFilterOptionProps> = ({
  selected,
  darkMode,
  cardType,
  onClick,
}) => {
  const { icon, gradientLight, gradientDark, glowColor, iconColor } = CARD_TYPE_CONFIG[cardType];

  const bg = darkMode ? gradientDark : gradientLight;

  /** Lighten a hex colour by `percent` (0–1). */
  const lightenColor = (hex: string, percent: number) => {
    const num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) + Math.round((255 - (num >> 16)) * percent);
    let g = ((num >> 8) & 0x00ff) + Math.round((255 - ((num >> 8) & 0x00ff)) * percent);
    let b = (num & 0x0000ff) + Math.round((255 - (num & 0x0000ff)) * percent);
    return `rgb(${Math.min(255, r)},${Math.min(255, g)},${Math.min(255, b)})`;
  };

  const backIconColor = lightenColor(iconColor, 0.4);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        gap: 0.75,
        opacity: selected ? 1 : 0.6,
      }}
    >
      {/* Circle */}
      <Box
        sx={{
          width: { xs: 64, sm: 74 },
          height: { xs: 64, sm: 74 },
          borderRadius: "50%",
          background: bg,
          border: darkMode
            ? `1px solid ${selected ? "rgba(99,102,241,0.4)" : "rgba(255,255,255,0.09)"}`
            : `1px solid ${selected ? "rgba(99,102,241,0.3)" : "rgba(0,0,0,0.07)"}`,
          boxShadow: darkMode
            ? selected
              ? `0 4px 20px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.04), 0 0 20px ${glowColor}`
              : `0 4px 20px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.04)`
            : selected
              ? `0 4px 18px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.06), 0 0 15px ${glowColor}`
              : `0 4px 18px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.06)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",

          "&:active": {
            transform: "scale(0.93)",
            transition: "transform 0.08s ease",
          },

          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            boxShadow: `inset 0 0 18px ${glowColor}`,
            opacity: selected ? 0.45 : 0.25,
            pointerEvents: "none",
          },
        }}
      >
        {/* Icon wrapper */}
        <Box
          sx={{
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            flexShrink: 0,
            zIndex: 1,
          }}
        >
          {/* Blurred back-layer for 3-D depth */}
          <Box
            sx={{
              position: "absolute",
              color: backIconColor,
              transform: "translate(2px,2px) scale(1.2)",
              zIndex: 0,
              filter: "blur(1px)",
            }}
          >
            {icon}
          </Box>

          {/* Main icon */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              color: iconColor,
              filter: `drop-shadow(0 2px 4px ${backIconColor}) drop-shadow(0 4px 10px ${glowColor})`,
            }}
          >
            {icon}
          </Box>

          {/* Radial halo */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: `radial-gradient(circle at 50% 40%, ${glowColor}40 0%, transparent 75%)`,
              zIndex: 0,
              filter: "blur(6px)",
            }}
          />
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: { xs: "0.6875rem", sm: "0.75rem" },
          color: darkMode ? "var(--neutral-400)" : "var(--neutral-600)",
          textAlign: "center",
          maxWidth: { xs: 64, sm: 74 },
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: '"Lexend", sans-serif',
          fontWeight: 600,
        }}
      >
        All
      </Typography>
    </Box>
  );
};

export default AllTalentFilterOption;
